import { ButtonClickAnalyticsType, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { ComponentType, useEffect, useState } from "react";
import { PostRegistrationActionsModal } from "./PostRegistrationActionsModal";
import { PostRegistrationActions } from "./PostRegistrationActions";
import styled from "@emotion/styled";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface PostRegistrationStepProps {
    updateStepMessage?: (componentState: any) => void;
}

export interface PostRegistrationActionsProps {
    /**
     * An array of steps to be displayed, each containing a component and its props.
     */
    postRegistrationSteps: KmsTypeComponent[];
    /**
     * Callback function to be called when the user completes all steps.
     */
    onPostRegistrationCompleted: () => void;

    /**
     * Whether to open the post-registration steps in a modal or as a list.
     */
    openInModal?: boolean;
}

const ComponentContainer = styled("div")(({ theme }) => ({
    minHeight: "calc(100vh - 90px)",
    padding: theme.spacing(4),
}));

const FixedBottomContainer = styled("div")(({ theme }) => ({
    position: "sticky",
    bottom: 0,
    height: 90,
    borderTop: `1px solid ${theme.kaltura.palette.tone5}`,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: theme.kaltura.palette.surfaces.background,
    padding: theme.spacing(0, 4),
}));

/**
 * PostRegistration component
 *
 * This component manages the post-registration steps for a registered user.
 * It displays a series of steps (inside modal or as a list),
 * each represented by a component, and allows the user to navigate through them.
 * The component also handles updating and displaying step-specific messages.
 *
 */
export const PostRegistration = ({
    postRegistrationSteps,
    onPostRegistrationCompleted,
    openInModal = true,
}: PostRegistrationActionsProps) => {
    const [activeStepIndex, setActiveStepIndex] = useState(0);
    const [stepMessage, setStepMessage] = useState();
    const isLastStep = activeStepIndex === postRegistrationSteps.length - 1;

    const [activeComponent, setActiveComponent] = useState<{ Component: ComponentType; props: any }>();

    // useEffect hook to update the active component whenever the active step index changes.
    // It retrieves the component and its props for the current step, sets the updateStepMessage function,
    // and updates the activeComponent state with the new component and its props.
    useEffect(() => {
        const { component: activeStepComponent, props } = postRegistrationSteps[activeStepIndex];
        const Component = getComponentFromPath(activeStepComponent);
        props.updateStepMessage = setStepMessage;
        setActiveComponent({ Component, props });
    }, [activeStepIndex, postRegistrationSteps]);

    const sendButtonAnalytics = useButtonAnalytics();
    const handleNext = () => {
        if (isLastStep) {
            onPostRegistrationCompleted();
            sendButtonAnalytics("Complete RSVP", ButtonClickAnalyticsType.CLOSE, undefined, "RSVP", "RSVP");
        }
        else {
            setActiveStepIndex(activeStepIndex + 1);
            setStepMessage(undefined);
        }
    };

    const postRegistrationActions = (
        <PostRegistrationActions onNext={handleNext} isLastStep={isLastStep} stepMessage={stepMessage} />
    );

    if (activeComponent) {
        return openInModal ? (
            <PostRegistrationActionsModal
                activeStepContent={<activeComponent.Component {...activeComponent.props} />}
                postRegistrationActions={postRegistrationActions}
            />
        ) : (
            <>
                <ComponentContainer className="kms-ds-post-registration-container">
                    <activeComponent.Component {...activeComponent.props} />
                </ComponentContainer>
                <FixedBottomContainer className="kms-ds-post-registration-bottom-container">
                    {postRegistrationActions}
                </FixedBottomContainer>
            </>
        );
    }
};
